import IconButton from "@/components/atoms/IconButton";
import Select from "@/components/atoms/Select";
import AuthCardLayout from "@/components/molecules/AuthCardLayout";
import { EMAIL_REGEX, PASSWORD_REGEX } from "@/constants/regexs";
import {
  INVALID_EMAIL2_MESSAGE,
  INVALID_EMAIL_MESSAGE,
  INVALID_FIRSTNAME_MESSAGE,
  INVALID_FORM_MESSAGE,
  INVALID_LASTNAME_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_PHONE2_MESSAGE,
} from "@/constants/validatorMessages";
import { matchPassword } from "@/helpers/FormHelpers";
import { IRegister } from "@/interfaces/IAuth";
import { USER_ROLES } from "@/interfaces/IUser";
import { useRegisterMutation } from "@/services/auth";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import classNames from "classnames";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { FC, useState } from "react";
import { Button, Form, InputGroup, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";

const RegisterView: FC = () => {
  const navigation = useNavigate();
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<IRegister>();
  const [registerMutation] = useRegisterMutation();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const onSubmit: SubmitHandler<IRegister> = async ({
    phone,
    roles,
    ...registerData
  }) => {
    await toast.promise(
      registerMutation({
        ...registerData,
        phone: phone && parsePhoneNumber(phone, "FR")?.number,
        roles: roles.map((role) => role.value),
      }).unwrap(),
      {
        pending: "Envoi d'une demande en cours...",
        success: {
          render({ data }) {
            navigation("/auth/login");
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                Votre demande à bien été prise en compte, vous serez recontactez
                très prochainement.
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            const response = data?.data as any;
            if (data?.status === 422) {
              if (
                response.errors.some(
                  (element: any) => element.rule === "unique",
                )
              ) {
                return "Cet email ou ce numéro de téléphone est déjà utilisé.";
              }

              return "Votre demande n'a pas pu être prise en compte, veuillez vérifier les informations saisies.";
            }

            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  const onError: SubmitErrorHandler<IRegister> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <AuthCardLayout
      leftSideContent={
        <p className="text-white">
          Vous avez déjà un compte ?
          <br />
          <Link
            className="text-white text-decoration-underline"
            to="/auth/login"
          >
            Se connecter
          </Link>
        </p>
      }
    >
      <h3>Faire une demande</h3>
      <Form onSubmit={handleSubmit(onSubmit, onError)} className="mt-4">
        <Row className="gx-3">
          <Form.Group className="mb-3">
            <Form.Label>
              Prenom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : Remi"
              type="text"
              {...register("firstname", {
                required: INVALID_FIRSTNAME_MESSAGE,
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
                maxLength: {
                  value: 255,
                  message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.firstname}
            />
            <Form.Control.Feedback type="invalid">
              {errors.firstname?.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Nom de famille <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : Dupont"
              type="text"
              {...register("lastname", {
                required: INVALID_LASTNAME_MESSAGE,
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
                maxLength: {
                  value: 255,
                  message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.lastname}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lastname?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Email <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : remi@groupe-pi.com"
              type="email"
              {...register("email", {
                required: INVALID_EMAIL_MESSAGE,
                pattern: {
                  value: EMAIL_REGEX,
                  message: INVALID_EMAIL2_MESSAGE,
                },
              })}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Numéro de téléphone :</Form.Label>
            <Form.Control
              placeholder="ex : +33 6 34 83 55 32"
              type="text"
              {...register("phone", {
                validate: (value: string) => {
                  if (value && !isValidPhoneNumber(value, "FR")) {
                    return INVALID_PHONE2_MESSAGE;
                  }
                },
              })}
              isInvalid={!!errors.phone}
            />
            <Form.Control.Feedback type="invalid">
              {errors.phone?.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Role(s) <span className="text-danger">*</span> :
            </Form.Label>
            <Controller
              control={control}
              name="roles"
              rules={{
                required: "Le rôle ou les rôles sont requis.",
              }}
              render={({ field }) => (
                <Select
                  options={USER_ROLES.filter(
                    (role) => role.value !== "SUPERADMIN",
                  )}
                  value={field.value}
                  isMulti
                  onChange={field.onChange}
                />
              )}
            />
            <Form.Control.Feedback
              type="invalid"
              className={classNames({
                "d-block": !!errors.roles,
              })}
            >
              {errors.roles?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Mot de passe <span className="text-danger">*</span> :
            </Form.Label>
            <InputGroup>
              <Form.Control
                placeholder="ex : 98Koilju@1"
                type={showPassword ? "text" : "password"}
                {...register("password", {
                  pattern: {
                    value: PASSWORD_REGEX,
                    message:
                      "Votre mot de passe doit contenir au moins 8 caractères dont une majuscule, une minuscule et un chiffre.",
                  },
                  required: "Le mot de passe est obligatoire",
                })}
                isInvalid={!!errors.password}
              />
              <InputGroup.Text
                as={IconButton}
                variant="outline-secondary"
                icon={showPassword ? faEyeSlash : faEye}
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle-button"
              />
            </InputGroup>
            <Form.Control.Feedback type="invalid">
              {errors.password?.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Confirmer le mot de passe<span className="text-danger"> *</span> :
            </Form.Label>
            <InputGroup>
              <Form.Control
                placeholder="ex : 98Koilju@1"
                type={showPassword ? "text" : "password"}
                {...register("passwordConfirmation", {
                  required: "La confimation du mot de passe est obligatoire",
                  validate: (value) =>
                    matchPassword(value, watch("password")) ||
                    "Les mots de passe ne correspondent pas",
                })}
                isInvalid={!!errors.passwordConfirmation}
              />
              <InputGroup.Text
                as={IconButton}
                variant="outline-secondary"
                icon={showPassword ? faEyeSlash : faEye}
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle-button"
              />
            </InputGroup>
            {errors.passwordConfirmation && (
              <Form.Control.Feedback type="invalid">
                {errors.passwordConfirmation?.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Row>

        <Button type="submit" variant="primary" className="mt-3 w-100">
          Envoyer une demande
        </Button>
      </Form>
    </AuthCardLayout>
  );
};

export default RegisterView;
